// A card's face (spec §13.3): title, note, checklist with tick boxes, and a
// footer with the done/total count, due date and assignee avatars. Laid out
// once per content change as plain data, then drawn by the canvas (through the
// text raster cache, like any other text) or turned into SVG for export.
import type { Card, RichText } from '../../shared/types.ts';
import { CARD, cardGeometry, isOverdue, sortedItems } from '../../shared/cards.ts';
import { isEmptyRichText } from '../../shared/rich-text.ts';
import { peerColor } from '../../shared/protocol.ts';
import { FONT_FAMILY, fontString, layoutText, type Measure, type TextLayout } from '../text/layout.ts';
import { drawLayout, TEXT_COLOR, type Ctx2D } from '../text/draw.ts';

/** Who's who, for avatars. `version` changes whenever names do. */
export interface CardPeople {
  name(userId: string): string | null;
  readonly version: number;
}

export const NO_PEOPLE: CardPeople = { name: () => null, version: 0 };

const MUTED = '#5F6368';
const DONE_TEXT = '#9AA0A6';
const PLACEHOLDER = '#9AA0A6';
export const BOX_BORDER = '#9AA0A6';
export const BOX_DONE = '#30A46C';
const UNKNOWN_PERSON = '#C9CDD4';
export const CHIP_FONT_SIZE = 12;
const CHIP_FONT = fontString(CHIP_FONT_SIZE, 1);
const INITIALS_WEIGHT = '600';

export interface FaceText { layout: TextLayout; x: number; y: number; color: string; strike?: boolean }
export interface FaceBox { itemId: string; x: number; y: number; size: number; done: boolean }
export interface FaceChip { x: number; y: number; w: number; h: number; text: string; textX: number; fg: string; bg: string; icon: 'check' | 'due' }
export interface FaceAvatar { cx: number; cy: number; r: number; color: string; initials: string; ring: boolean }

export interface CardFace {
  height: number;
  texts: FaceText[];
  boxes: FaceBox[];
  chips: FaceChip[];
  avatars: FaceAvatar[];
}

const plain = (t: string, bold = false): RichText => ({ blocks: [{ runs: [bold ? { t, b: 1 } : { t }] }] });

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  const first = Array.from(words[0]!)[0] ?? '';
  const last = words.length > 1 ? Array.from(words[words.length - 1]!)[0] ?? '' : '';
  return (first + last).toUpperCase();
}

function avatar(userId: string, people: CardPeople, cx: number, cy: number, r: number, ring: boolean): FaceAvatar {
  const name = people.name(userId);
  // People who have since left (or aren't known yet) get a neutral placeholder.
  return name === null
    ? { cx, cy, r, color: UNKNOWN_PERSON, initials: '', ring }
    : { cx, cy, r, color: peerColor(userId), initials: initials(name), ring };
}

/** "12 Oct", with the year when it isn't this year. */
export function formatDue(due: string, today: string): string {
  const [y, m, d] = due.split('-').map(Number) as [number, number, number];
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  if (due.slice(0, 4) !== today.slice(0, 4)) opts.year = 'numeric';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, opts);
}

export function layoutCard(card: Card, measure: Measure, people: CardPeople, today: string): CardFace {
  const inner = Math.max(40, card.w - 2 * CARD.pad);
  const hasTitle = !!card.title.trim();
  const title = layoutText(plain(hasTitle ? card.title : 'Untitled card', true), { width: inner, fontSize: CARD.titleSize, align: 'left' }, measure);
  const note = card.note && !isEmptyRichText(card.note)
    ? layoutText(card.note, { width: inner, fontSize: CARD.noteSize, align: 'left' }, measure) : null;
  const items = sortedItems(card);
  const itemLayouts = items.map(([, it]) => {
    const avatarW = it.assignee ? CARD.itemAvatar + CARD.itemAvatarGap : 0;
    const w = Math.max(20, inner - CARD.box - CARD.boxGap - avatarW);
    return layoutText(plain(it.text), { width: w, fontSize: CARD.itemSize, align: 'left' }, measure);
  });
  const g = cardGeometry(card, {
    title: title.lines.length, note: note?.lines.length ?? 0, items: itemLayouts.map((l) => l.lines.length),
  });

  const face: CardFace = { height: g.height, texts: [], boxes: [], chips: [], avatars: [] };
  face.texts.push({ layout: title, x: CARD.pad, y: g.titleY, color: hasTitle ? TEXT_COLOR : PLACEHOLDER });
  if (note && g.noteY !== null) face.texts.push({ layout: note, x: CARD.pad, y: g.noteY, color: MUTED });

  const firstLine = CARD.itemSize * CARD.lineHeight;
  items.forEach(([id, it], i) => {
    const y = g.itemYs[i]!;
    const mid = y + firstLine / 2;
    face.boxes.push({ itemId: id, x: CARD.pad, y: mid - CARD.box / 2, size: CARD.box, done: it.done });
    face.texts.push({
      layout: itemLayouts[i]!, x: CARD.pad + CARD.box + CARD.boxGap, y,
      color: it.done ? DONE_TEXT : TEXT_COLOR, strike: it.done,
    });
    if (it.assignee) {
      const r = CARD.itemAvatar / 2;
      face.avatars.push(avatar(it.assignee, people, card.w - CARD.pad - r, mid, r, false));
    }
  });

  if (g.footerY !== null) {
    const y = g.footerY, hgt = CARD.footerH;
    let x = CARD.pad;
    const chip = (text: string, fg: string, bg: string, icon: FaceChip['icon']) => {
      const textX = x + 7 + 11 + 5;
      const w = textX - x + measure(text, CHIP_FONT) + 8;
      face.chips.push({ x, y, w, h: hgt, text, textX, fg, bg, icon });
      x += w + 6;
    };
    if (items.length) {
      const done = items.filter(([, it]) => it.done).length;
      const complete = done === items.length;
      chip(`${done}/${items.length}`, complete ? '#188038' : MUTED, complete ? '#E6F4EA' : '#F1F3F4', 'check');
    }
    if (card.due) {
      const late = isOverdue(card, today);
      chip(formatDue(card.due, today), late ? '#D93025' : MUTED, late ? '#FDECEA' : '#F1F3F4', 'due');
    }
    // Avatars from the right edge; past the limit the last slot says "+N".
    const r = CARD.avatar / 2;
    const cy = y + hgt / 2;
    const n = card.assignees.length;
    const shown = n > CARD.maxAvatars ? CARD.maxAvatars - 1 : n;
    const step = CARD.avatar - CARD.avatarOverlap;
    let cx = card.w - CARD.pad - r;
    if (n > shown) {
      face.avatars.push({ cx, cy, r, color: '#E8EAED', initials: `+${n - shown}`, ring: true });
      cx -= step;
    }
    for (let i = shown - 1; i >= 0; i--) {
      face.avatars.push(avatar(card.assignees[i]!, people, cx, cy, r, true));
      cx -= step;
    }
  }
  return face;
}

/** The checklist item whose tick box is at card-local (lx, ly), with some slack. */
export function boxAt(face: CardFace, lx: number, ly: number, slack = 4): string | null {
  for (const b of face.boxes) {
    if (lx >= b.x - slack && lx <= b.x + b.size + slack && ly >= b.y - slack && ly <= b.y + b.size + slack) return b.itemId;
  }
  return null;
}

// --- canvas ---

export function drawCardFace(ctx: Ctx2D, face: CardFace, scale: number): void {
  const px = 1 / scale;
  for (const t of face.texts) {
    drawLayout(ctx, t.layout, t.x, t.y, t.color, scale);
    if (!t.strike) continue;
    ctx.fillStyle = t.color;
    for (const ln of t.layout.lines) {
      if (ln.w > 0) ctx.fillRect(t.x + ln.x, t.y + ln.y - 0.5, ln.w, Math.max(1.1, px));
    }
  }

  for (const b of face.boxes) {
    ctx.beginPath();
    ctx.roundRect(b.x, b.y, b.size, b.size, 3);
    if (b.done) {
      ctx.fillStyle = BOX_DONE;
      ctx.fill();
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 1.8;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(b.x + b.size * 0.25, b.y + b.size * 0.52);
      ctx.lineTo(b.x + b.size * 0.43, b.y + b.size * 0.7);
      ctx.lineTo(b.x + b.size * 0.76, b.y + b.size * 0.32);
      ctx.stroke();
    } else {
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();
      ctx.strokeStyle = BOX_BORDER;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }

  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  for (const c of face.chips) {
    ctx.fillStyle = c.bg;
    ctx.beginPath();
    ctx.roundRect(c.x, c.y, c.w, c.h, c.h / 2);
    ctx.fill();
    drawChipIcon(ctx, c);
    ctx.font = CHIP_FONT;
    ctx.fillStyle = c.fg;
    ctx.fillText(c.text, c.textX, c.y + c.h / 2);
  }

  ctx.textAlign = 'center';
  for (const a of face.avatars) {
    ctx.beginPath();
    ctx.arc(a.cx, a.cy, a.r, 0, Math.PI * 2);
    ctx.fillStyle = a.color;
    ctx.fill();
    if (a.ring) {
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    if (!a.initials) continue;
    ctx.font = `${INITIALS_WEIGHT} ${Math.round(a.r * 0.9 * 10) / 10}px ${FONT_FAMILY}`;
    ctx.fillStyle = a.initials.startsWith('+') ? MUTED : '#FFFFFF';
    ctx.fillText(a.initials, a.cx, a.cy + a.r * 0.04);
  }
  ctx.textAlign = 'left';
}

/** Top-left of a chip's 11 × 11 icon. */
export const chipIconAt = (c: FaceChip): [number, number] => [c.x + 7, c.y + (c.h - 11) / 2];

function drawChipIcon(ctx: Ctx2D, c: FaceChip): void {
  const [x, y] = chipIconAt(c);
  const s = 11;
  ctx.strokeStyle = c.fg;
  ctx.lineWidth = 1.3;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  if (c.icon === 'check') {
    ctx.roundRect(x, y, s, s, 2.5);
    ctx.moveTo(x + 2.8, y + 5.8);
    ctx.lineTo(x + 4.8, y + 7.8);
    ctx.lineTo(x + 8.3, y + 3.6);
  } else {
    ctx.roundRect(x, y + 1, s, s - 1, 2);
    ctx.moveTo(x, y + 4.5);
    ctx.lineTo(x + s, y + 4.5);
    ctx.moveTo(x + 3, y);
    ctx.lineTo(x + 3, y + 2);
    ctx.moveTo(x + 8, y);
    ctx.lineTo(x + 8, y + 2);
  }
  ctx.stroke();
}
